import React, { useState, useMemo, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import useInvoice from '../hooks/useInvoice';
import useInvoiceCalculations from '../hooks/useInvoiceCalculations';
import { useGlobalContext } from '../hooks/useGlobalContext';
import InvoiceMainForm from './InvoiceMainForm';
import InvoiceSidebar from './InvoiceSidebar';
import InvoicePrintPreview from './InvoicePrintPreview';
import { useTranslation } from 'react-i18next';

const InvoiceForm = () => {
  const invoiceState = useInvoice();
  const { subTotal, taxAmount, discountAmount, total } = useInvoiceCalculations(invoiceState.items, invoiceState.taxRate, invoiceState.discountRate);
  const [isPrinting, setIsPrinting] = useState(false);
  const { saveInvoiceToHistory, currentInvoice } = useGlobalContext(); // Updated: Use saveInvoiceToHistory
  const { t } = useTranslation();

  // useEffect to trigger re-initialization when currentInvoice changes (handled in useInvoice)
  useEffect(() => {
    // This ensures the form updates when an invoice is loaded; logic is in useInvoice for encapsulation
  }, [currentInvoice]);

  // Memoized validation for required fields to enable/disable the print button.
  const isFormValid = useMemo(() => {
    const hasRequiredFields =
      invoiceState.billTo.trim() &&
      invoiceState.billFrom.trim() &&
      invoiceState.invoiceNumber.trim() &&
      invoiceState.invoiceDate &&
      invoiceState.dateOfIssue;
    const hasValidItem = invoiceState.items.some(
      (item) => item.description.trim() && item.price >= 0 && item.quantity >= 0
    );
    return hasRequiredFields && hasValidItem;
  }, [invoiceState]);

  // Handler for direct PDF print from the hidden styled preview.
  const handlePrint = async () => {
    setIsPrinting(true);
    try {
      const element = document.querySelector("#invoicePrintPreview");
      if (!element) {
        alert(t('captureError'));
        return;
      }
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [612, 792]
      });
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`invoice-${invoiceState.invoiceNumber || '001'}.pdf`);
      
      // Construct the current invoice data
      const currentInvoiceData = {
        billTo: invoiceState.billTo,
        billToEmail: invoiceState.billToEmail,
        billToAddress: invoiceState.billToAddress,
        billFrom: invoiceState.billFrom,
        billFromEmail: invoiceState.billFromEmail,
        billToDocumentId: invoiceState.billToDocumentId,
        billFromAddress: invoiceState.billFromAddress,
        notes: invoiceState.notes,
        termsAndConditions: invoiceState.termsAndConditions,
        billFromDocumentId: invoiceState.billFromDocumentId,
        dateOfIssue: invoiceState.dateOfIssue,
        invoiceNumber: invoiceState.invoiceNumber,
        invoiceDate: invoiceState.invoiceDate,
        items: invoiceState.items,
        currency: invoiceState.currency,
        taxRate: invoiceState.taxRate,
        discountRate: invoiceState.discountRate,
        logo: invoiceState.logo,
        subTotal,
        taxAmount,
        discountAmount,
        total,
        // Note: balanceDue is not stored separately; it's derived from total
      };

      // Updated: Use unified saveInvoiceToHistory from context
      saveInvoiceToHistory(currentInvoiceData);
    } catch (error) {
      alert(t('pdfError'));
      console.error("PDF generation error:", error);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Form onSubmit={invoiceState.openModal}>
      <Row>
        {/* Main form content extracted to InvoiceMainForm */}
        <Col md={8} lg={9}>
          <InvoiceMainForm
            invoiceState={invoiceState}
            subTotal={subTotal}
            discountAmount={discountAmount}
            taxAmount={taxAmount}
            total={total}
            balanceDue={total}  // Updated: Pass total as balanceDue (ensures it's always the total and read-only)
          />
        </Col>
        {/* Sidebar extracted to InvoiceSidebar */}
        <Col md={4} lg={3}>
          <InvoiceSidebar
            invoiceState={invoiceState}
            subTotal={subTotal}
            taxAmount={taxAmount}
            discountAmount={discountAmount}
            total={total}
            isPrinting={isPrinting}
            isFormValid={isFormValid}
            handlePrint={handlePrint}
          />
        </Col>
      </Row>
      {/* PDF preview extracted to InvoicePrintPreview */}
      <InvoicePrintPreview
        invoiceState={invoiceState}
        subTotal={subTotal}
        taxAmount={taxAmount}
        discountAmount={discountAmount}
        total={total}
        balanceDue={total}  // New: Pass total as balanceDue for PDF inclusion (derived value)
      />
    </Form>
  );
};

export default InvoiceForm;